/**
 * Invoice Preview Component
 * Branded invoice preview matching PDF output layout.
 * Used in create/edit (live preview) and in detail view.
 */

import React from 'react';
import { useFormattedDate } from '../../hooks/useUserTimezone';

interface PreviewLineItem {
  item_name?: string;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
  installment_number?: number;
  total_installments?: number;
  total_contract_amount?: number;
}

interface InvoicePreviewData {
  invoice_number: string;
  customer_name: string;
  customer_company?: string;
  customer_email?: string;
  customer_address?: string;
  invoice_date?: string;
  due_date?: string;
  payment_terms?: string;
  line_items: PreviewLineItem[];
  subtotal: number;
  discount_amount: number;
  tax_amount: number;
  total: number;
  memo?: string;
  notes?: string;
  payment_instructions?: string;
  organization_name: string;
  status?: string;
  portal_url?: string;
  show_portal_link?: boolean;
  bank_details?: {
    bank_name?: string;
    account_name?: string;
    account_number?: string;
    routing_number?: string;
    swift_code?: string;
  };
  accepted_payment_methods?: string[];
  payment_method_instructions?: Record<string, string>;
  branding?: {
    logo_url?: string;
    primary_color?: string;
    company_name?: string;
  };
}

interface InvoicePreviewProps {
  data: InvoicePreviewData;
  printMode?: boolean;
}

/** Render formatted description text: **bold**, - bullets, # headings, indentation */
function FormattedText({ text, className }: { text: string; className?: string }) {
  if (!text) return null;
  const lines = text.split('\n');
  return (
    <div className={className}>
      {lines.map((line, i) => {
        const stripped = line.trim();
        if (!stripped) return <br key={i} />;
        const indent = line.length - line.trimStart().length;
        const style = indent >= 2 ? { paddingLeft: `${indent * 4}px` } : undefined;

        let content: React.ReactNode = stripped;

        // Heading
        if (stripped.startsWith('# ')) {
          content = <strong>{stripped.slice(2)}</strong>;
        }
        // Bullet
        else if (stripped.startsWith('- ') || stripped.startsWith('• ')) {
          content = <>&bull; {applyBold(stripped.slice(2))}</>;
        }
        else if (stripped.startsWith('* ') && !stripped.startsWith('**')) {
          content = <>&bull; {applyBold(stripped.slice(2))}</>;
        }
        else {
          content = applyBold(stripped);
        }

        return <div key={i} style={style}>{content}</div>;
      })}
    </div>
  );
}

/** Convert **bold** markers to <strong> elements */
function applyBold(text: string): React.ReactNode {
  const parts = text.split(/(\*\*.+?\*\*)/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

export function InvoicePreview({ data, printMode = false }: InvoicePreviewProps) {
  const { fmt } = useFormattedDate();
  const primaryColor = data.branding?.primary_color || '#3B82F6';
  const companyName = data.branding?.company_name || data.organization_name;

  const fmtUSD = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return fmt.long(dateStr);
  };

  const isDraft = data.status === 'draft';

  // Calculate remaining to bill from installment items
  const totalContractValue = data.line_items.reduce((sum, item) => {
    if (item.total_installments && item.total_installments > 1 && item.total_contract_amount) {
      return sum + item.total_contract_amount;
    }
    return sum;
  }, 0);
  const totalBilledThisInvoice = data.line_items.reduce((sum, item) => {
    if (item.total_installments && item.total_installments > 1 && item.total_contract_amount) {
      return sum + item.amount;
    }
    return sum;
  }, 0);
  const remainingToBill = totalContractValue - totalBilledThisInvoice;
  const hasInstallments = totalContractValue > 0;

  const statusBadge = data.status ? (
    <span className={`
      inline-block px-2 py-0.5 text-xs font-bold uppercase rounded
      ${data.status === 'draft' ? 'bg-amber-100 text-amber-700 border border-amber-300' :
        data.status === 'paid' ? 'bg-green-100 text-green-700' :
        data.status === 'overdue' ? 'bg-red-100 text-red-700' :
        data.status === 'void' ? 'bg-gray-100 text-gray-700' :
        'bg-blue-100 text-blue-700'}
    `}>
      {data.status}
    </span>
  ) : null;

  return (
    <div className={`bg-white rounded-lg border border-gray-200 shadow-sm relative ${printMode ? '' : 'max-h-[calc(100vh-200px)] overflow-y-auto'}`}
      id="invoice-preview-printable">
      {/* DRAFT watermark */}
      {isDraft && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 overflow-hidden">
          <span className="text-[80px] font-black text-gray-200/60 uppercase tracking-[0.2em] select-none" style={{ transform: 'rotate(-30deg)' }}>
            DRAFT
          </span>
        </div>
      )}
      {/* Header */}
      <div className="px-8 pt-8 pb-4" style={{ borderBottom: `3px solid ${primaryColor}` }}>
        <div className="flex justify-between items-start">
          <div>
            {data.branding?.logo_url ? (
              <img src={data.branding.logo_url} alt={companyName} className="h-12 mb-2" />
            ) : null}
            <h2 className="text-lg font-bold text-gray-900">{companyName}</h2>
          </div>
          <div className="text-right">
            <h1 className="text-2xl font-bold" style={{ color: primaryColor }}>INVOICE</h1>
            <p className="text-sm text-gray-600 mt-1">{data.invoice_number}</p>
            {statusBadge && <div className="mt-2">{statusBadge}</div>}
          </div>
        </div>
      </div>

      {/* Invoice Info + Bill To */}
      <div className="px-8 py-5 grid grid-cols-2 gap-6">
        <div className="space-y-1.5">
          <div className="flex gap-2 text-sm">
            <span className="text-gray-500 w-24">Invoice Date:</span>
            <span className="text-gray-900 font-medium">{formatDate(data.invoice_date)}</span>
          </div>
          <div className="flex gap-2 text-sm">
            <span className="text-gray-500 w-24">Due Date:</span>
            <span className="text-gray-900 font-medium">{formatDate(data.due_date)}</span>
          </div>
          {data.payment_terms && (
            <div className="flex gap-2 text-sm">
              <span className="text-gray-500 w-24">Terms:</span>
              <span className="text-gray-900">{data.payment_terms}</span>
            </div>
          )}
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Bill To</p>
          <p className="text-sm font-medium text-gray-900">{data.customer_name}</p>
          {data.customer_company && (
            <p className="text-sm text-gray-600">{data.customer_company}</p>
          )}
          {data.customer_email && (
            <p className="text-sm text-gray-600">{data.customer_email}</p>
          )}
          {data.customer_address && (
            <p className="text-sm text-gray-600 whitespace-pre-line">{data.customer_address}</p>
          )}
        </div>
      </div>

      {/* Line Items Table */}
      <div className="px-8 py-2">
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: primaryColor }}>
              <th className="px-3 py-2 text-left text-xs font-semibold text-white">Item</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-white">Description</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-white w-16">Qty</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-white w-24">Price</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-white w-24">Amount</th>
            </tr>
          </thead>
          <tbody>
            {data.line_items.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-400 italic">
                  No line items added yet
                </td>
              </tr>
            ) : (
              data.line_items.map((item, idx) => (
                <React.Fragment key={idx}>
                  <tr className={idx % 2 === 1 ? 'bg-gray-50' : ''}>
                    <td className="px-3 py-2 text-sm text-gray-900 font-medium">
                      <FormattedText text={item.item_name || item.description} />
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-600">
                      {item.item_name ? <FormattedText text={item.description} /> : ''}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-700 text-right">{item.quantity}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 text-right">{fmtUSD(item.unit_price)}</td>
                    <td className="px-3 py-2 text-sm text-gray-900 text-right font-medium">{fmtUSD(item.amount)}</td>
                  </tr>
                  {item.total_installments && item.total_installments > 1 && (
                    <tr className={idx % 2 === 1 ? 'bg-gray-50' : ''}>
                      <td colSpan={5} className="px-3 pb-2 pt-0">
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">
                            Installment {item.installment_number || 1} of {item.total_installments}
                          </span>
                          {item.total_contract_amount != null && item.total_contract_amount > 0 && (
                            <span>
                              Total Contract: {fmtUSD(item.total_contract_amount)}
                              {' '}&middot;{' '}
                              Remaining: {fmtUSD(item.total_contract_amount - item.amount)}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="px-8 py-4">
        <div className="flex justify-end">
          <div className="w-64 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Subtotal</span>
              <span className="text-gray-900">{fmtUSD(data.subtotal)}</span>
            </div>
            {data.discount_amount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Discount</span>
                <span className="text-red-600">-{fmtUSD(data.discount_amount)}</span>
              </div>
            )}
            {data.tax_amount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Tax</span>
                <span className="text-gray-900">{fmtUSD(data.tax_amount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm font-bold pt-2 border-t border-gray-300" style={{ color: primaryColor }}>
              <span>Total Due</span>
              <span>{fmtUSD(data.total)}</span>
            </div>
            {hasInstallments && remainingToBill > 0 && (
              <div className="flex justify-between text-xs pt-2 mt-1 border-t border-dashed border-gray-200">
                <span className="text-gray-400">Remaining to Bill</span>
                <span className="text-gray-500 font-medium">{fmtUSD(remainingToBill)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Memo / Payment Instructions */}
      {(data.memo || data.payment_instructions) && (
        <div className="px-8 py-4 border-t border-gray-200 space-y-3">
          {data.memo && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Memo</p>
              <FormattedText text={data.memo} className="text-sm text-gray-700" />
            </div>
          )}
          {data.payment_instructions && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Payment Instructions</p>
              <FormattedText text={data.payment_instructions} className="text-sm text-gray-700" />
            </div>
          )}
        </div>
      )}

      {/* Bank / Payment Details */}
      {(data.bank_details?.bank_name || (data.accepted_payment_methods && data.accepted_payment_methods.length > 0)) && (
        <div className="px-8 py-4 border-t border-gray-200">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Payment Details</p>
          {data.bank_details?.bank_name && (
            <div className="text-sm text-gray-700 space-y-0.5 mb-2">
              <p className="font-medium">{data.bank_details.bank_name}</p>
              {data.bank_details.account_name && <p>Account: {data.bank_details.account_name}</p>}
              {data.bank_details.account_number && <p>Account #: {data.bank_details.account_number}</p>}
              {data.bank_details.routing_number && <p>Routing #: {data.bank_details.routing_number}</p>}
              {data.bank_details.swift_code && <p>SWIFT: {data.bank_details.swift_code}</p>}
            </div>
          )}
          {data.accepted_payment_methods && data.accepted_payment_methods.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {data.accepted_payment_methods.map(m => (
                <span key={m} className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 capitalize">
                  {m.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}
          {data.payment_method_instructions && data.accepted_payment_methods && data.accepted_payment_methods.some(m => data.payment_method_instructions?.[m]) && (
            <div className="mt-3 space-y-2">
              {data.accepted_payment_methods.filter(m => data.payment_method_instructions?.[m]).map(m => (
                <div key={m} className="text-sm">
                  <p className="font-medium text-gray-700 capitalize">{m.replace(/_/g, ' ')}</p>
                  <p className="text-gray-600 text-xs mt-0.5">{data.payment_method_instructions![m]}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Payment Portal Link */}
      {data.show_portal_link !== false && data.portal_url && (
        <div className="px-8 py-4 border-t border-gray-200">
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: primaryColor + '08', border: `1px solid ${primaryColor}30` }}>
            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor + '15' }}>
              <svg className="w-4 h-4" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: primaryColor }}>Pay Online</p>
              <p className="text-xs text-gray-500 truncate mt-0.5">{data.portal_url}</p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-8 py-4 bg-gray-50 rounded-b-lg border-t border-gray-200">
        <p className="text-xs text-gray-400 text-center">
          Generated by {companyName} via WeaveIn.ai
        </p>
      </div>
    </div>
  );
}
